import type { ToolUIPart } from 'ai';
import { isToolUIPart, type UIDataTypes, type UIMessagePart, type UITools } from 'ai';
import type { ReactNode } from 'react';

import { BrowserUseTool } from './browser-use-tool';
import { ClarificationTool } from './clarification-tool';
import { EmailDraftReviewTool } from './email-draft-review-tool';
import { FillSl1FormTool } from './fill-sl1-form-tool';
import { FillSl2FormTool } from './fill-sl2-form-tool';
import { FormFillingTool } from './form-filling-tool';
import { ImageGenerationTool } from './image-generation-tool';
import { NovaScheduleProposalTool } from './nova-schedule-proposal-tool';
import { ProcessFilesTool } from './process-files-tool';
import { renderToolPendingState } from './tool-pending-state';
import type { ToolOutputRenderer } from '../../lib/default-renderers';

type PendingToolState = 'input-streaming' | 'input-available';

const DEFAULT_PENDING_STATES: readonly PendingToolState[] = ['input-streaming', 'input-available'];

interface ToolRendererWrapperProps {
  part: UIMessagePart<UIDataTypes, UITools>;
  pendingStates?: readonly PendingToolState[];
  toolName: string;
  children: ReactNode;
}

function isPendingToolState(state: ToolUIPart['state']): state is PendingToolState {
  return state === 'input-streaming' || state === 'input-available';
}

/**
 * Wrapper component that handles common tool rendering logic:
 * - Validates tool part
 * - Handles pending states
 * - Renders children when output is available
 */
function ToolRendererWrapper({
  part,
  pendingStates = DEFAULT_PENDING_STATES,
  toolName,
  children,
}: ToolRendererWrapperProps) {
  if (!isToolUIPart(part)) return null;

  // Handle pending state
  if (isPendingToolState(part.state) && pendingStates.includes(part.state)) {
    return renderToolPendingState(toolName);
  }

  // Render children (the actual tool component) when output is available
  return children;
}

export interface CreateToolRendererOptions {
  toolName: string;
  Component: React.ComponentType<{ part: ToolUIPart; messageUuid: string }>;
  pendingStates?: readonly PendingToolState[];
}

/**
 * Creates a tool renderer function with common logic handled by ToolRendererWrapper.
 */
export function createToolRenderer({
  toolName,
  Component,
  pendingStates,
}: CreateToolRendererOptions): ToolOutputRenderer {
  return function ToolRenderer(part, messageUuid) {
    return (
      <ToolRendererWrapper part={part} pendingStates={pendingStates} toolName={toolName}>
        <Component part={part as ToolUIPart} messageUuid={messageUuid} />
      </ToolRendererWrapper>
    );
  };
}

/**
 * Creates default tool output renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 */
export function createDefaultToolRenderers(): Record<string, ToolOutputRenderer> {
  return {
    browserUse: createToolRenderer({
      toolName: 'browserUse',
      Component: BrowserUseTool,
      pendingStates: ['input-streaming'],
    }),
    clarification: createToolRenderer({
      toolName: 'clarification',
      Component: ClarificationTool,
      pendingStates: ['input-streaming'],
    }),
    proposeNovaSchedule: createToolRenderer({
      toolName: 'proposeNovaSchedule',
      Component: NovaScheduleProposalTool,
      pendingStates: [],
    }),
    processFiles: createToolRenderer({
      toolName: 'processFiles',
      Component: ProcessFilesTool,
    }),
    emailDraftReview: createToolRenderer({
      toolName: 'emailDraftReview',
      Component: EmailDraftReviewTool,
      pendingStates: ['input-streaming'],
    }),
    fillSl1Form: createToolRenderer({
      toolName: 'fillSl1Form',
      Component: FillSl1FormTool,
    }),
    fillSl2Form: createToolRenderer({
      toolName: 'fillSl2Form',
      Component: FillSl2FormTool,
    }),
    formFilling: createToolRenderer({
      toolName: 'formFilling',
      Component: FormFillingTool,
    }),
    image_generation: createToolRenderer({
      toolName: 'image_generation',
      Component: ImageGenerationTool,
    }),
  };
}
